import {Clock, Coffee, Keyboard, MapPin, Pencil, Plus, Sparkles, Trash2, X} from 'lucide-react'
import React, {FC, useEffect, useId, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {IntlShape, useIntl} from 'react-intl'

import {WizardData} from '../index'
import {ONBOARDING_QA_A11Y} from '../utils/onboardingQaA11y'
import {ONBOARDING_QA_OCT18} from '../utils/onboardingQaOct18'
import {branchIdOf, isFirstBranch, removeBranches, renameBranch} from '../utils/pendingDeletes'
import {
  DAY_LETTERS,
  DEFAULT_BRANCH_NAME,
  DEFAULT_LECTURE_DURATION,
  DEFAULT_TIMING_DAYS,
  DEFAULT_TIMING_END,
  DEFAULT_TIMING_START,
  WEEKDAYS,
  formatTime24to12,
  getMidpointBreak,
  normalizeDay,
  timeToMinutes,
} from './Step2BasicSetup'
import BranchRemovalDialog, {BranchRemovalRequest} from './BranchRemovalDialog'
import WizardDatePicker from './WizardDatePicker'
import WizardTimePicker from './WizardTimePicker'

/** The Institution Setup sections the review page's edit sheet can host. */
export type EditableSection =
  | 'branches'
  | 'duration'
  | 'frequency'
  | 'venues'
  | 'timings'
  | 'breaks'

type ManualData = WizardData['manualData']
type TimingRow = {day: string; startTime: string; endTime: string; lectureDuration?: number}
type BreakRow = {day: string; startTime: string; endTime: string}
/** One start/end pair in the break form; a day may hold several. */
type BreakSlot = {startTime: string; endTime: string}

/** Structural equality for the editors' dirty checks (draft vs. what the sheet opened with). */
const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

const minutesToTime = (mins: number) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`

/** Stable order for break rows: weekday first, then start time. */
const sortBreaks = (list: BreakRow[]): BreakRow[] =>
  [...list].sort((a, b) => {
    const dayDiff =
      WEEKDAYS.indexOf(normalizeDay(a.day) as (typeof WEEKDAYS)[number]) -
      WEEKDAYS.indexOf(normalizeDay(b.day) as (typeof WEEKDAYS)[number])
    return dayDiff !== 0 ? dayDiff : timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  })

// Same bounds the legacy Step2BasicSetup section enforces on the native date inputs.
const SESSION_DATE_MIN = '0001-01-01'
const SESSION_DATE_MAX = '9999-12-31'

interface Props {
  section: EditableSection
  data: WizardData
  updateData: (data: Partial<WizardData>, options?: {prefill?: boolean}) => void
  isDark: boolean
  /** Close the hosting sheet (after a commit, or on Cancel). */
  onDone: () => void
  /** Branch saves also run the incremental branches sync, like the old sub-step advance did. */
  onPartialSubmit?: (fields: any[], manualData?: ManualData, cb?: () => void) => void
  isSubmitting?: boolean
}

/** Shared color tokens so every section editor reads as one surface. */
const makeTokens = (isDark: boolean) => ({
  isDark,
  text: isDark ? '#ffffff' : '#181C32',
  muted: isDark ? '#92929f' : '#7E8299',
  border: isDark ? '#3f4254' : '#e4e6ef',
  panelBg: isDark ? 'rgba(255,255,255,0.04)' : '#f9f9fb',
  inputBg: isDark ? '#1e1e2e' : '#ffffff',
  accent: '#056EE9',
  danger: '#f1416c',
})
type Tokens = ReturnType<typeof makeTokens>

const inputStyle = (t: Tokens): React.CSSProperties => ({
  background: t.inputBg,
  color: t.text,
  border: `1px solid ${t.border}`,
  borderRadius: 10,
  padding: '0.6rem 0.75rem',
  outline: 'none',
  width: '100%',
  fontSize: '0.95rem',
})

const labelStyle = (t: Tokens): React.CSSProperties => ({
  color: t.muted,
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: '0.4rem',
  display: 'block',
})

const helperStyle = (t: Tokens): React.CSSProperties => ({
  color: t.muted,
  fontSize: '0.875rem',
  lineHeight: 1.6,
  marginBottom: 0,
})

const errorStyle = (t: Tokens): React.CSSProperties => ({
  color: t.danger,
  fontSize: '0.875rem',
  fontWeight: 600,
  marginBottom: 0,
})

// Dark mode goes white-on-translucent: the blue-on-blue variant is unreadable
// over the sheet's dark gradient.
const chipStyle = (t: Tokens): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.35rem 0.75rem',
  borderRadius: 999,
  border: t.isDark ? '1.5px solid rgba(255,255,255,0.45)' : `1.5px solid ${t.accent}`,
  background: t.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(5,110,233,0.08)',
  color: t.isDark ? '#ffffff' : t.accent,
  fontSize: '0.9rem',
  fontWeight: 600,
})

/** Muted micro-caps header cells above the timings/breaks lists. */
const listHeaderCellStyle = (t: Tokens): React.CSSProperties => ({
  color: t.muted,
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
})

/** Accent-tinted badge showing the row's day name. */
const dayBadgeStyle = (t: Tokens): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.3rem 0.55rem',
  borderRadius: 8,
  background: 'rgba(5,110,233,0.1)',
  color: t.isDark ? '#6ea8f7' : t.accent,
  fontWeight: 700,
  fontSize: '0.78rem',
  whiteSpace: 'nowrap',
})

/** Quiet outlined pill (lecture duration, counts). */
const pillStyle = (t: Tokens): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.2rem 0.55rem',
  borderRadius: 999,
  border: `1px solid ${t.border}`,
  color: t.muted,
  fontSize: '0.78rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
})

/**
 * Round day toggle, shared by the timings and breaks forms. With ONBOARDING_QA_A11Y it is named
 * by the full day (`name`), reports its state with aria-pressed, and an unavailable day stays
 * focusable (aria-disabled) with `disabledReason` as its description instead of only a tooltip.
 */
const DayChip: FC<{
  label: string
  /** Full day name, the chip's accessible name (the visible label is one letter). */
  name?: string
  selected: boolean
  disabled: boolean
  title?: string
  /** Why the day can't be picked right now, said to screen readers (and shown on hover). */
  disabledReason?: string
  onToggle: () => void
  tokens: Tokens
}> = ({label, name, selected, disabled, title, disabledReason, onToggle, tokens: t}) => {
  const reasonId = useId()
  const reason = ONBOARDING_QA_A11Y && disabled ? disabledReason : undefined
  return (
    <>
      <button
        type='button'
        onClick={ONBOARDING_QA_A11Y && disabled ? undefined : onToggle}
        disabled={ONBOARDING_QA_A11Y ? undefined : disabled}
        aria-disabled={ONBOARDING_QA_A11Y && disabled ? true : undefined}
        aria-pressed={ONBOARDING_QA_A11Y ? selected : undefined}
        aria-label={ONBOARDING_QA_A11Y ? name : undefined}
        aria-describedby={reason ? reasonId : undefined}
        title={title ?? reason}
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          border: `2px solid ${selected ? t.accent : t.border}`,
          background: selected ? t.accent : 'transparent',
          color: selected ? '#ffffff' : disabled ? `${t.muted}80` : t.muted,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontWeight: 600,
          fontSize: '0.85rem',
        }}
      >
        {label}
      </button>
      {reason && (
        <span id={reasonId} className='visually-hidden'>
          {reason}
        </span>
      )}
    </>
  )
}

/**
 * Cancel (left) / Save (right) footer, flowing directly under the section's
 * content — same layout as the wizard's Back/Next. Sticky keeps it reachable
 * while long content scrolls. Styled by .step2-edit-sheet-* in
 * _onboardingwizard.scss. Every editor keeps Save disabled until its draft
 * differs from what the sheet opened with, so an untouched sheet cannot commit.
 */
const SheetFooter: FC<{
  onCancel: () => void
  onSave: () => void
  saveDisabled: boolean
  isSubmitting: boolean
  tokens: Tokens
  intl: IntlShape
  /** Drop the Save button entirely (nothing to commit yet); Cancel stays. */
  hideSave?: boolean
  /**
   * QA 18 Oct (timings/breaks only): pin the footer to the bottom of the sheet with its own
   * surface (.step2-edit-sheet-footer--fixed) so the single Save/Cancel pair stays reachable.
   */
  fixed?: boolean
  /** Accessible name of the footer's button group (what Save/Cancel act on). */
  ariaLabel?: string
}> = ({
  onCancel,
  onSave,
  saveDisabled,
  isSubmitting,
  intl,
  hideSave = false,
  fixed = false,
  ariaLabel,
}) => (
  <div
    className={fixed ? 'step2-edit-sheet-footer step2-edit-sheet-footer--fixed' : 'step2-edit-sheet-footer'}
    role={ariaLabel ? 'group' : undefined}
    aria-label={ariaLabel}
  >
    <button
      type='button'
      className='step2-edit-sheet-cancel'
      onClick={onCancel}
      disabled={isSubmitting}
    >
      {intl.formatMessage({id: 'GENERAL.CANCEL'})}
    </button>
    {!hideSave && (
      <button
        type='button'
        className='step2-edit-sheet-save'
        onClick={onSave}
        disabled={saveDisabled || isSubmitting}
      >
        {isSubmitting && <span className='spinner-border spinner-border-sm align-middle me-2' />}
        {intl.formatMessage({id: 'GENERAL.SAVE'})}
      </button>
    )}
  </div>
)

/** Root layout every section editor shares. */
const editorRootProps = {
  className: 'd-flex flex-column gap-4 pt-4',
}

/**
 * QA 18 Oct root for the timings/breaks editors: same layout, stretched to the sheet's height so
 * the fixed footer sits at the bottom even when the table is short.
 */
const fixedFooterRootProps = {
  className: `${editorRootProps.className} step2-edit-sheet-editor--fixed-footer`,
}

/**
 * Scroll the SheetModal body that hosts `el` back to its top. The sheet's `.card-body` is the
 * scroller; without one (e.g. a test render) the element is scrolled into view instead.
 *
 * @param el Any element inside the sheet (the editor root).
 */
const scrollSheetToTop = (el: HTMLElement | null) => {
  if (!el) return
  const scroller = el.closest('.card-body') as HTMLElement | null
  if (scroller) scroller.scrollTop = 0
  else el.scrollIntoView?.({block: 'start'})
}

/**
 * QA 18 Oct: the timings/breaks editors swap the table for the form (and back) inside the sheet.
 * Every swap starts the sheet at the top, and an opened form moves focus to its heading so
 * keyboard and screen-reader users land in it. Inert while ONBOARDING_QA_OCT18 is off.
 *
 * @param view Identifies what the editor shows (null = table; anything else = that form).
 * @returns Refs for the editor root and the form heading (the heading needs tabIndex={-1}).
 */
const useTableFormSwap = (view: unknown) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!ONBOARDING_QA_OCT18) return
    scrollSheetToTop(rootRef.current)
    if (view !== null) headingRef.current?.focus({preventScroll: true})
  }, [view])
  return {rootRef, headingRef}
}

/**
 * Per-section "don't ask again" persistence for the delete confirmation.
 * Stored per editor section so skipping in Timings does not silence Breaks.
 */
const skipDeleteConfirmKey = (section: string) => `onboarding_step2_skip_delete_confirm:${section}`
const readSkipDeleteConfirm = (section: string): boolean => {
  try {
    return localStorage.getItem(skipDeleteConfirmKey(section)) === 'true'
  } catch {
    return false
  }
}
const writeSkipDeleteConfirm = (section: string) => {
  try {
    localStorage.setItem(skipDeleteConfirmKey(section), 'true')
  } catch {
    // Storage unavailable — the dialog simply keeps asking.
  }
}

/**
 * Delete confirmation, styled to the sheet (the shared MUI ConfirmationModal
 * carries hardcoded English and a different design language). Portals above the
 * sheet's z-index; backdrop click or Cancel dismisses. `onConfirm` receives the
 * "do not show again" checkbox state so the host can stop asking.
 */
const ConfirmDeleteDialog: FC<{
  open: boolean
  onCancel: () => void
  onConfirm: (dontShowAgain: boolean) => void
  tokens: Tokens
  intl: IntlShape
  /** What else the delete removes (a day's breaks), said before it happens. */
  warning?: string | null
}> = ({open, onCancel, onConfirm, tokens: t, intl, warning}) => {
  const [dontShow, setDontShow] = useState(false)
  const checkboxId = useId()
  const titleId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (open) setDontShow(false)
  }, [open])
  // QA 6c: focus lands on Cancel (the safe choice), Escape cancels, and focus goes back to the
  // row's Delete button when that is still there (a confirmed delete removes it).
  const onCancelRef = useRef(onCancel)
  useEffect(() => {
    onCancelRef.current = onCancel
  })
  useEffect(() => {
    if (!ONBOARDING_QA_A11Y || !open) return
    const opener = document.activeElement as HTMLElement | null
    cancelRef.current?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      onCancelRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (opener?.isConnected) opener.focus()
    }
  }, [open])
  if (!open) return null
  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={ONBOARDING_QA_A11Y ? titleId : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1080,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onCancel}
        style={{position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)'}}
      />
      <div
        style={{
          position: 'relative',
          width: 'min(360px, calc(100vw - 2rem))',
          borderRadius: 14,
          border: `1px solid ${t.border}`,
          background: t.isDark ? '#1a1b23' : '#ffffff',
          padding: '1.4rem',
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
        }}
      >
        <div className='d-flex align-items-center gap-2 mb-2'>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'rgba(241,65,108,0.12)',
              color: t.danger,
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} />
          </span>
          <span id={titleId} style={{color: t.text, fontWeight: 700, fontSize: '1rem'}}>
            {intl.formatMessage({id: 'MENU.ARE_YOU_SURE?'})}
          </span>
        </div>
        <p style={{...helperStyle(t), marginBottom: '0.9rem'}}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.DELETE_ROW_CONFIRM'})}
        </p>
        {warning && (
          <p role='alert' style={{...errorStyle(t), marginBottom: '0.9rem'}}>
            {warning}
          </p>
        )}
        <label
          htmlFor={checkboxId}
          className='d-flex align-items-center gap-2'
          style={{cursor: 'pointer', marginBottom: '1.1rem', userSelect: 'none'}}
        >
          <input
            id={checkboxId}
            type='checkbox'
            className='form-check-input step2-delete-confirm-check'
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            style={{width: 16, height: 16, marginTop: 0, cursor: 'pointer', flexShrink: 0}}
          />
          <span style={{color: t.muted, fontSize: '0.85rem'}}>
            {intl.formatMessage({id: 'ALERT.DO_NOT_SHOW_AGAIN'})}
          </span>
        </label>
        <div className='d-flex justify-content-end gap-2'>
          <button
            ref={cancelRef}
            type='button'
            className='step2-edit-sheet-cancel step2-edit-sheet-btn-sm'
            onClick={onCancel}
          >
            {intl.formatMessage({id: 'GENERAL.CANCEL'})}
          </button>
          <button
            type='button'
            onClick={() => onConfirm(dontShow)}
            style={{
              border: 'none',
              borderRadius: 10,
              padding: '0.45rem 1.1rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#ffffff',
              background: t.danger,
              boxShadow: '0 6px 16px rgba(241,65,108,0.35)',
            }}
          >
            {intl.formatMessage({id: 'GENERAL.DELETE'})}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * The Institution Setup review page's edit-sheet content: a compact, sheet-native
 * editor per section. Unlike the old modal (which embedded the whole
 * Step2BasicSetup flow through CSS scope hacks), each editor here works on a
 * LOCAL DRAFT seeded from the wizard state and commits it in one `updateData`
 * call on Save — closing without saving discards the draft. Data contracts,
 * validation rules and i18n keys mirror Step2BasicSetup exactly, so both UIs
 * stay interchangeable; Step2BasicSetup itself is untouched and still serves
 * the flows that embed it elsewhere.
 */
const Step2SectionEditor: FC<Props> = ({
  section,
  data,
  updateData,
  isDark,
  onDone,
  onPartialSubmit,
  isSubmitting = false,
}) => {
  const intl = useIntl()
  const t = makeTokens(isDark)
  const md = data.manualData

  /** Merge the section's fields into manualData as ONE user edit, then close. */
  const commitAndClose = (fields: Partial<ManualData>, opts?: {syncBranches?: boolean}) => {
    const merged = {...md, ...fields}
    updateData({manualData: merged})
    if (opts?.syncBranches && onPartialSubmit) {
      onPartialSubmit(['branches'], merged, onDone)
    } else {
      onDone()
    }
  }

  const common = {intl, tokens: t, onCancel: onDone, isSubmitting}

  switch (section) {
    case 'branches':
      return (
        <BranchesEditor
          {...common}
          // Names only: state persisted by an older build may still hold `{name, id}` entries.
          initial={((md?.branches ?? []) as unknown[])
            .map((entry: any) =>
              String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim()
            )
            .filter(Boolean)}
          isSaved={(name) => branchIdOf(md, name) !== null}
          isFirst={(name) => isFirstBranch(md, name)}
          onSave={(branches, renames) => {
            // Renames keep the branch's server id (sent as an update); a saved branch left out
            // of the list is queued for delete, its classes and allocations dropped with it.
            const renamed = renames.reduce((acc, [from, to]) => renameBranch(acc, from, to), md)
            const kept = new Set(branches.map((name) => name.toLowerCase()))
            const removed = ((renamed?.branches ?? []) as unknown[])
              .map((entry: any) =>
                String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim()
              )
              .filter((name) => name && !kept.has(name.toLowerCase()))
            commitAndClose({...removeBranches(renamed, removed), branches}, {syncBranches: true})
          }}
        />
      )
    case 'duration':
      return (
        <SessionEditor
          {...common}
          initial={{
            name: md?.durationName ?? '',
            start: md?.durationStartDate ?? '',
            end: md?.durationEndDate ?? '',
          }}
          onSave={({name, start, end}) =>
            commitAndClose({durationName: name, durationStartDate: start, durationEndDate: end})
          }
        />
      )
    case 'frequency':
      return (
        <FrequencyEditor
          {...common}
          initial={{
            count: md?.defaultSubjectFrequency ?? 0,
            per: md?.defaultFrequencyPer ?? 'week',
          }}
          onSave={({count, per}) =>
            commitAndClose({defaultSubjectFrequency: count, defaultFrequencyPer: per})
          }
        />
      )
    case 'venues':
      return (
        <VenuesEditor
          {...common}
          initial={{
            same: md?.venuesSameForClass ?? null,
            venues: md?.venues ?? [],
            autoCount: md?.venuesAutoCount ?? 0,
          }}
          onSave={({same, venues, autoCount}) => {
            // Same side effect the legacy count handler kept for the seeder.
            if (same === false && autoCount > 0) {
              localStorage.setItem('onboarding_venues_auto_count', String(autoCount))
            } else {
              localStorage.removeItem('onboarding_venues_auto_count')
            }
            commitAndClose({
              venuesSameForClass: same,
              venues: same === false ? venues : [],
              venuesCount: same === false ? venues.length : 0,
              venuesAutoCount: same === false ? autoCount : 0,
            })
          }}
        />
      )
    case 'timings': {
      const breaks = ((md as any)?.break_timings ?? []) as Array<{day?: string}>
      /** The fields a timings save writes (and the breaks of dropped days it removes). */
      const timingsFields = (timings: TimingRow[]): Partial<ManualData> => {
        // A break belongs to its day's school hours, so a day dropped from the timings
        // takes its breaks with it (the editor warned about them). Days that keep timings
        // keep their breaks untouched, even when the hours changed.
        const kept = new Set(timings.map((row) => normalizeDay(row.day)))
        const nextBreaks = breaks.filter((b) => kept.has(normalizeDay(b?.day)))
        return {
          timings: timings as ManualData['timings'],
          ...(nextBreaks.length !== breaks.length
            ? ({break_timings: nextBreaks} as Partial<ManualData>)
            : {}),
        }
      }
      return (
        <TimingsEditor
          {...common}
          initial={(md?.timings ?? []) as TimingRow[]}
          breakDays={Array.from(new Set(breaks.map((b) => normalizeDay(b?.day))))}
          onSave={(timings) => commitAndClose(timingsFields(timings))}
          // QA 18 Oct: the form's Save saves the section and keeps the sheet open on the table.
          onCommit={(timings) => updateData({manualData: {...md, ...timingsFields(timings)}})}
        />
      )
    }
    case 'breaks':
      return (
        <BreaksEditor
          {...common}
          initial={((md as any)?.break_timings ?? []).map((b: any) => ({
            day: String(b.day ?? 'MONDAY'),
            startTime: b.startTime ?? '',
            endTime: b.endTime ?? '',
          }))}
          timings={(md?.timings ?? []) as TimingRow[]}
          onSave={(break_timings) => commitAndClose({break_timings} as Partial<ManualData>)}
          // QA 18 Oct: the form's Save saves the section and keeps the sheet open on the table.
          onCommit={(break_timings) =>
            updateData({manualData: {...md, ...({break_timings} as Partial<ManualData>)}})
          }
        />
      )
    default:
      return null
  }
}

interface EditorCommon {
  intl: IntlShape
  tokens: Tokens
  onCancel: () => void
  isSubmitting: boolean
}

/* ------------------------------- Branches ---------------------------------- */

const BranchesEditor: FC<
  EditorCommon & {
    initial: string[]
    /** `renames` are `[from, to]` pairs in the order they were made. */
    onSave: (branches: string[], renames: Array<[string, string]>) => void
    /** The branch is on the server: removing it deletes its data, so it asks first. */
    isSaved: (name: string) => boolean
    /** The school's first branch: it can't be deleted, only renamed. */
    isFirst: (name: string) => boolean
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave, isSaved, isFirst}) => {
  const [branches, setBranches] = useState<string[]>(initial)
  const [renames, setRenames] = useState<Array<[string, string]>>([])
  const [request, setRequest] = useState<BranchRemovalRequest | null>(null)
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputId = useId()

  // A chip renamed in this sheet still is the saved branch it started as.
  const savedNameOf = (name: string) => {
    let current = name
    for (let i = renames.length - 1; i >= 0; i--) {
      if (renames[i][1] === current) current = renames[i][0]
    }
    return current
  }
  const requestRemove = (name: string) => {
    const saved = savedNameOf(name)
    if (isFirst(saved)) setRequest({name, mode: 'rename'})
    else if (isSaved(saved)) setRequest({name, mode: 'remove'})
    else setBranches((prev) => prev.filter((b) => b !== name))
  }

  // A name still typed in the box counts as a change: Save adds it.
  const dirty = Boolean(input.trim()) || !sameJson(branches, initial)

  const tryAdd = (): boolean => {
    const name = input.trim()
    if (!name) return true
    // Same rule as Step2BasicSetup: letters, numbers, spaces and hyphens only.
    if (!/^[\p{L}\p{N}\s-]+$/u.test(name)) {
      setError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_NAME_INVALID'}))
      return false
    }
    if (branches.some((b) => b.toLowerCase() === name.toLowerCase())) {
      setError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_DUPLICATE_ERROR'}))
      setInput('')
      return false
    }
    setError(null)
    setBranches((prev) => [...prev, name])
    setInput('')
    return true
  }

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_INSTRUCTION'})}
      </p>
      <div>
        <label style={labelStyle(t)} htmlFor={inputId}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES'})}
        </label>
        <div
          onClick={() => document.getElementById(inputId)?.focus()}
          style={{
            ...inputStyle(t),
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'text',
            padding: '0.5rem 0.75rem',
          }}
        >
          {branches.map((name, idx) => (
            <span key={`${name}-${idx}`} style={chipStyle(t)}>
              {name}
              {(branches.length === 1 || name !== DEFAULT_BRANCH_NAME) && (
                <button
                  type='button'
                  aria-label='Remove'
                  className='step2-edit-sheet-chip-remove'
                  onClick={(e) => {
                    e.stopPropagation()
                    requestRemove(name)
                  }}
                >
                  <X size={13} strokeWidth={3} />
                </button>
              )}
            </span>
          ))}
          <input
            id={inputId}
            type='text'
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                tryAdd()
              } else if (e.key === 'Backspace' && input === '' && branches.length > 0) {
                // Same rule as the chip's × button: the default branch only
                // goes when it is the last one standing.
                const last = branches[branches.length - 1]
                if (branches.length === 1 || last !== DEFAULT_BRANCH_NAME) {
                  e.preventDefault()
                  requestRemove(last)
                }
              }
            }}
            placeholder={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_INPUT_PLACEHOLDER',
            })}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              flex: 1,
              minWidth: 120,
              color: t.text,
              fontSize: '0.95rem',
            }}
          />
        </div>
        {error && (
          <p
            className='mt-2'
            role={ONBOARDING_QA_A11Y ? 'alert' : undefined}
            style={errorStyle(t)}
          >
            {error}
          </p>
        )}
      </div>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_SINGLE_CAMPUS_NOTE'})}
      </p>
      <SheetFooter
        onCancel={onCancel}
        onSave={() => {
          // A name still sitting in the input counts — validate and include it.
          const name = input.trim()
          let final = branches
          if (name) {
            if (!/^[\p{L}\p{N}\s-]+$/u.test(name)) {
              setError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_NAME_INVALID'}))
              return
            }
            if (!branches.some((b) => b.toLowerCase() === name.toLowerCase())) {
              final = [...branches, name]
            }
          }
          if (final.length > 0) onSave(final, renames)
        }}
        saveDisabled={!dirty || (branches.length === 0 && !input.trim())}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
      <BranchRemovalDialog
        request={request}
        branches={branches}
        isDark={t.isDark}
        onCancel={() => setRequest(null)}
        onRemove={(name) => {
          setBranches((prev) => prev.filter((b) => b !== name))
          setRequest(null)
        }}
        onRename={(from, to) => {
          setBranches((prev) => prev.map((b) => (b === from ? to : b)))
          setRenames((prev) => [...prev, [from, to]])
          setRequest(null)
        }}
      />
    </div>
  )
}

/* ------------------------------- Session ----------------------------------- */

const SessionEditor: FC<
  EditorCommon & {
    initial: {name: string; start: string; end: string}
    onSave: (v: {name: string; start: string; end: string}) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave}) => {
  const [name, setName] = useState(initial.name)
  const [start, setStart] = useState(initial.start)
  const [end, setEnd] = useState(initial.end)
  const nameId = useId()
  const startId = useId()
  const endId = useId()

  const rangeInvalid = Boolean(start && end && end <= start)
  const valid = Boolean(name.trim() && start && end && !rangeInvalid)
  const dirty =
    name.trim() !== initial.name.trim() || start !== initial.start || end !== initial.end

  const dateField = (
    id: string,
    labelId: string,
    value: string,
    onChange: (v: string) => void,
    min: string
  ) => (
    <div style={{flex: 1, minWidth: 150}}>
      <label style={labelStyle(t)} htmlFor={id}>
        {intl.formatMessage({id: labelId as any})}
      </label>
      <WizardDatePicker
        id={id}
        min={min}
        max={SESSION_DATE_MAX}
        value={value}
        onChange={onChange}
        ariaLabel={intl.formatMessage({id: labelId as any})}
        style={{background: t.inputBg, color: t.text, border: `1px solid ${t.border}`}}
      />
    </div>
  )

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_HELPER'})}
      </p>
      <div>
        <label style={labelStyle(t)} htmlFor={nameId}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_NAME_LABEL'})}
        </label>
        <input
          id={nameId}
          type='text'
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP2.DURATION_NAME_PLACEHOLDER',
          })}
          style={inputStyle(t)}
        />
      </div>
      <div className='d-flex gap-3 flex-wrap'>
        {dateField(
          startId,
          'ONBOARDING_WIZARD.STEP2.SESSION_START_DATE_LABEL',
          start,
          setStart,
          SESSION_DATE_MIN
        )}
        {dateField(
          endId,
          'ONBOARDING_WIZARD.STEP2.SESSION_END_DATE_LABEL',
          end,
          setEnd,
          start || SESSION_DATE_MIN
        )}
      </div>
      {rangeInvalid && (
        <p role={ONBOARDING_QA_A11Y ? 'alert' : undefined} style={errorStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_END_AFTER_START'})}
        </p>
      )}
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_INFO'})}
      </p>
      <SheetFooter
        onCancel={onCancel}
        onSave={() => valid && onSave({name: name.trim(), start, end})}
        saveDisabled={!valid || !dirty}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
    </div>
  )
}

/* ------------------------------ Frequency ---------------------------------- */

const FrequencyEditor: FC<
  EditorCommon & {
    initial: {count: number; per: 'week' | 'day'}
    onSave: (v: {count: number; per: 'week' | 'day'}) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave}) => {
  const [count, setCount] = useState(initial.count)
  const [per, setPer] = useState<'week' | 'day'>(initial.per)
  const dirty = count !== initial.count || per !== initial.per

  const stepBtn = (delta: number, label: string) => (
    <button
      type='button'
      aria-label={label}
      onClick={() => setCount((c) => Math.max(1, Math.min(20, (c || 0) + delta)))}
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.inputBg,
        color: t.text,
        fontSize: '1.2rem',
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {label}
    </button>
  )

  const segBtn = (value: 'week' | 'day', labelId: string) => (
    <button
      type='button'
      onClick={() => setPer(value)}
      aria-pressed={per === value}
      style={{
        flex: 1,
        padding: '0.55rem 1rem',
        borderRadius: 8,
        border: 'none',
        background: per === value ? t.accent : 'transparent',
        color: per === value ? '#ffffff' : t.muted,
        fontWeight: 600,
        fontSize: '0.9rem',
        transition: 'all 0.15s ease',
      }}
    >
      {intl.formatMessage({id: labelId as any})}
    </button>
  )

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.FREQUENCY_INSTRUCTION'})}
      </p>
      <div className='d-flex align-items-center gap-3 flex-wrap'>
        {stepBtn(-1, '−')}
        <div
          style={{
            minWidth: 64,
            textAlign: 'center',
            fontSize: '1.6rem',
            fontWeight: 700,
            color: t.text,
          }}
        >
          {count || '—'}
        </div>
        {stepBtn(1, '+')}
        <span style={{color: t.muted}}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_PER'})}
        </span>
        <div
          className='d-flex'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 10,
            padding: 3,
            background: t.inputBg,
            minWidth: 190,
          }}
        >
          {segBtn('week', 'ONBOARDING_WIZARD.STEP2.TIMINGS_FREQUENCY_PER_WEEK')}
          {segBtn('day', 'ONBOARDING_WIZARD.STEP2.TIMINGS_FREQUENCY_PER_DAY')}
        </div>
      </div>
      <SheetFooter
        onCancel={onCancel}
        onSave={() => count >= 1 && count <= 20 && onSave({count, per})}
        saveDisabled={!dirty || count < 1 || count > 20}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
    </div>
  )
}

/* -------------------------------- Venues ----------------------------------- */

const VenuesEditor: FC<
  EditorCommon & {
    initial: {same: boolean | null; venues: Array<{name: string}>; autoCount: number}
    onSave: (v: {same: boolean | null; venues: Array<{name: string}>; autoCount: number}) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave}) => {
  const [same, setSame] = useState<boolean | null>(initial.same)
  const [venues, setVenues] = useState(initial.venues)
  const [autoCount, setAutoCount] = useState(initial.autoCount)
  const [input, setInput] = useState('')
  const manualInputId = useId()

  // One entry mode at a time (the legacy section greyed the other input out
  // instead); both drafts survive switching, Save commits only the active one.
  const [entryMode, setEntryMode] = useState<'auto' | 'manual'>(
    initial.venues.length > 0 ? 'manual' : 'auto'
  )

  // Compare what Save would commit against what the sheet opened with. "Same
  // venues" drops the list and count either way, so both sides normalise to that.
  const commitShape = (v: {
    same: boolean | null
    venues: Array<{name: string}>
    autoCount: number
  }) =>
    v.same === false
      ? {same: false, venues: v.venues.map((x) => x.name), autoCount: v.autoCount}
      : {same: v.same, venues: [], autoCount: 0}
  const dirty =
    Boolean(input.trim()) ||
    !sameJson(
      commitShape({
        same,
        venues: entryMode === 'manual' ? venues : [],
        autoCount: entryMode === 'auto' ? autoCount : 0,
      }),
      commitShape(initial)
    )

  const optionCard = (value: boolean, titleId: string, descId: string) => {
    const selected = same === value
    return (
      <button
        type='button'
        onClick={() => setSame(value)}
        aria-pressed={selected}
        style={{
          flex: 1,
          minWidth: 200,
          textAlign: 'left',
          borderRadius: 12,
          padding: '1rem 1.1rem',
          border: `2px solid ${selected ? t.accent : t.border}`,
          background: selected ? 'rgba(5,110,233,0.06)' : 'transparent',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{color: t.text, fontWeight: 700, fontSize: '0.98rem', marginBottom: '0.25rem'}}>
          {intl.formatMessage({id: titleId as any})}
        </div>
        <div style={{color: t.muted, fontSize: '0.85rem', lineHeight: 1.5}}>
          {intl.formatMessage({id: descId as any})}
        </div>
      </button>
    )
  }

  const addVenue = () => {
    const name = input.trim()
    if (!name) return
    if (venues.some((v) => v.name === name)) {
      setInput('')
      return
    }
    setVenues((prev) => [...prev, {name}])
    setInput('')
  }

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_QUESTION'})}
      </p>
      <div className='d-flex gap-3 flex-wrap'>
        {optionCard(
          true,
          'ONBOARDING_WIZARD.STEP2.VENUES_SAME',
          'ONBOARDING_WIZARD.STEP2.VENUES_SAME_DESC'
        )}
        {optionCard(
          false,
          'ONBOARDING_WIZARD.STEP2.VENUES_DIFFERENT',
          'ONBOARDING_WIZARD.STEP2.VENUES_DIFFERENT_DESC'
        )}
      </div>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_APPLIED_INFO'})}{' '}
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_APPLIED_CONSTRAINTS'})}
      </p>

      {same === false && (
        <div
          className='d-flex flex-column gap-3'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 14,
            padding: '1.1rem 1.2rem',
            background: t.panelBg,
          }}
        >
          <div className='d-flex align-items-center gap-2'>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'rgba(5,110,233,0.1)',
                color: t.isDark ? '#6ea8f7' : t.accent,
                flexShrink: 0,
              }}
            >
              <MapPin size={15} />
            </span>
            <span style={{color: t.text, fontWeight: 700, fontSize: '0.95rem', flex: 1}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_LIST_TITLE'})}
            </span>
            {(entryMode === 'manual' ? venues.length : autoCount) > 0 && (
              <span style={pillStyle(t)}>
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP2.VENUES_COUNT_LABEL'},
                  {count: entryMode === 'manual' ? venues.length : autoCount}
                )}
              </span>
            )}
          </div>

          <div
            className='d-flex'
            role='tablist'
            style={{
              border: `1px solid ${t.border}`,
              borderRadius: 10,
              padding: 3,
              background: t.inputBg,
            }}
          >
            {(
              [
                ['auto', Sparkles, 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_AUTO'],
                ['manual', Keyboard, 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_MANUAL'],
              ] as const
            ).map(([value, Icon, labelId]) => (
              <button
                key={value}
                type='button'
                role='tab'
                aria-selected={entryMode === value}
                onClick={() => setEntryMode(value)}
                className='d-inline-flex align-items-center justify-content-center gap-2'
                style={{
                  flex: 1,
                  padding: '0.55rem 1rem',
                  borderRadius: 8,
                  border: 'none',
                  background: entryMode === value ? t.accent : 'transparent',
                  color: entryMode === value ? '#ffffff' : t.muted,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  transition: 'all 0.15s ease',
                }}
              >
                <Icon size={15} />
                {intl.formatMessage({id: labelId})}
              </button>
            ))}
          </div>

          {entryMode === 'auto' ? (
            <div className='d-flex flex-column gap-3'>
              <p style={helperStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_AUTO_DESC'})}
              </p>
              <div className='d-flex align-items-center gap-3 flex-wrap'>
                {([-1, 1] as const).map((delta) => (
                  <button
                    key={delta}
                    type='button'
                    aria-label={delta === 1 ? 'Increase' : 'Decrease'}
                    onClick={() => setAutoCount((c) => Math.max(0, Math.min(999, c + delta)))}
                    disabled={delta === -1 ? autoCount <= 0 : autoCount >= 999}
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      border: `1px solid ${t.border}`,
                      background: t.inputBg,
                      color: t.text,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      order: delta === 1 ? 2 : 0,
                    }}
                  >
                    {delta === 1 ? '+' : '−'}
                  </button>
                ))}
                <input
                  type='number'
                  min={0}
                  max={999}
                  value={autoCount || ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    setAutoCount(isNaN(v) ? 0 : Math.max(0, Math.min(999, v)))
                  }}
                  placeholder={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP2.VENUES_COUNT_PLACEHOLDER',
                  })}
                  style={{...inputStyle(t), width: 170, textAlign: 'center', order: 1}}
                />
              </div>
              {autoCount > 0 && (
                <div className='d-flex flex-wrap gap-2'>
                  {Array.from({length: Math.min(autoCount, 6)}, (_, i) => (
                    <span key={i} style={pillStyle(t)}>
                      Venue {i + 1}
                    </span>
                  ))}
                  {autoCount > 6 && <span style={pillStyle(t)}>+{autoCount - 6}</span>}
                </div>
              )}
            </div>
          ) : (
            <div className='d-flex flex-column gap-3'>
              <p style={helperStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_MANUAL_DESC'})}
              </p>
              <div
                onClick={() => document.getElementById(manualInputId)?.focus()}
                style={{
                  ...inputStyle(t),
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'text',
                  padding: '0.5rem 0.75rem',
                }}
              >
                {venues.map((v, idx) => (
                  <span key={`${v.name}-${idx}`} style={chipStyle(t)}>
                    {v.name}
                    <button
                      type='button'
                      aria-label='Remove'
                      className='step2-edit-sheet-chip-remove'
                      onClick={(e) => {
                        e.stopPropagation()
                        setVenues((prev) => prev.filter((_, i) => i !== idx))
                      }}
                    >
                      <X size={13} strokeWidth={3} />
                    </button>
                  </span>
                ))}
                <input
                  id={manualInputId}
                  type='text'
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault()
                      addVenue()
                    } else if (e.key === 'Backspace' && input === '' && venues.length > 0) {
                      e.preventDefault()
                      setVenues((prev) => prev.slice(0, -1))
                    }
                  }}
                  onBlur={addVenue}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    flex: 1,
                    minWidth: 120,
                    color: t.text,
                    fontSize: '0.95rem',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <SheetFooter
        onCancel={onCancel}
        onSave={() =>
          same !== null &&
          onSave({
            same,
            venues: entryMode === 'manual' ? venues : [],
            autoCount: entryMode === 'auto' ? autoCount : 0,
          })
        }
        saveDisabled={!dirty || same === null}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
    </div>
  )
}

/* -------------------------------- Timings ---------------------------------- */

/** The longest lecture the timings form takes, in minutes. */
const MAX_LECTURE_MINUTES = 240

const TimingsEditor: FC<
  EditorCommon & {
    initial: TimingRow[]
    /** Normalised days that currently have a saved break, for the removal warning. */
    breakDays: string[]
    onSave: (timings: TimingRow[]) => void
    /** QA 18 Oct: save the list without closing the sheet (the form's Save). */
    onCommit?: (timings: TimingRow[]) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, breakDays, onSave, onCommit}) => {
  const [rows, setRows] = useState<TimingRow[]>(initial)
  // null = form closed; -1 = adding; BULK_EDIT = editing all rows at once;
  // >= 0 = editing that row
  const BULK_EDIT = -2
  const [formFor, setFormFor] = useState<number | null>(initial.length === 0 ? -1 : null)
  const {rootRef, headingRef} = useTableFormSwap(formFor)
  // Row index awaiting delete confirmation, or null.
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null)
  // "Do not show again" ticked earlier (persisted): delete rows without asking.
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(() => readSkipDeleteConfirm('timings'))

  const deleteRow = (idx: number) => {
    const next = rows.filter((_, i) => i !== idx)
    setRows(next)
    if (formFor === idx) setFormFor(null)
    // QA: a delete saves at once, as the form's Save does (it used to wait for the footer's Save
    // and was lost when the sheet closed). The last day stays a draft: no timings can't be saved.
    if (ONBOARDING_QA_OCT18 && next.length > 0) onCommit?.(next)
  }
  /** Breaks the deleted day takes with it: said in the delete's confirmation (it saves at once). */
  const deleteBreakWarning = (idx: number | null): string | null => {
    if (!ONBOARDING_QA_OCT18 || idx === null) return null
    const day = normalizeDay(rows[idx]?.day)
    if (!day || !breakDays.includes(day)) return null
    return intl.formatMessage(
      {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DELETE_DROPS_BREAKS'},
      {day: intl.formatMessage({id: `DAY.${day}` as any})}
    )
  }
  const [form, setForm] = useState({
    selectedDays: [] as string[],
    startTime: DEFAULT_TIMING_START,
    endTime: DEFAULT_TIMING_END,
    lectureDuration: DEFAULT_LECTURE_DURATION,
  })
  const [formError, setFormError] = useState<string | null>(null)
  const durationInputId = useId()
  const daysLabelId = useId()

  const usedDays = useMemo(() => new Set(rows.map((r) => normalizeDay(r.day))), [rows])

  const openAdd = () => {
    setForm({
      selectedDays: DEFAULT_TIMING_DAYS.filter((d) => !usedDays.has(normalizeDay(d))),
      startTime: DEFAULT_TIMING_START,
      endTime: DEFAULT_TIMING_END,
      lectureDuration: DEFAULT_LECTURE_DURATION,
    })
    setFormError(null)
    setFormFor(-1)
  }

  const openEdit = (idx: number) => {
    const r = rows[idx]
    setForm({
      selectedDays: [r.day],
      startTime: r.startTime,
      endTime: r.endTime,
      lectureDuration: r.lectureDuration ?? 0,
    })
    setFormError(null)
    setFormFor(idx)
  }

  /**
   * Bulk edit: one form pre-selecting every current day, whose Save REPLACES the
   * whole list — one time span and lecture duration applied to all selected days.
   * Only days that already have a row are toggleable (so a day can be dropped,
   * but new days such as Saturday/Sunday are added via "Add", not here).
   * Seeded from the first row.
   */
  const openBulkEdit = () => {
    const first = rows[0]
    setForm({
      selectedDays: rows.map((r) => normalizeDay(r.day)),
      startTime: first?.startTime ?? DEFAULT_TIMING_START,
      endTime: first?.endTime ?? DEFAULT_TIMING_END,
      lectureDuration: first?.lectureDuration ?? DEFAULT_LECTURE_DURATION,
    })
    setFormError(null)
    setFormFor(BULK_EDIT)
  }

  // Bulk mode enables only days that already have a row; edit mode locks the
  // chips to the edited row's day; add mode disables days that already have a
  // row — same rules as the legacy timing modal.
  const isDayDisabled = (day: string) => {
    if (formFor === BULK_EDIT) return !usedDays.has(normalizeDay(day))
    if (formFor !== null && formFor >= 0) {
      return normalizeDay(day) !== normalizeDay(rows[formFor]?.day)
    }
    return usedDays.has(normalizeDay(day))
  }

  const applyForm = () => {
    const {selectedDays, startTime, endTime, lectureDuration} = form
    if (!startTime || !endTime || selectedDays.length === 0) return
    if (timeToMinutes(endTime) - timeToMinutes(startTime) < (lectureDuration ?? 0)) {
      setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_SPAN_TOO_SHORT'}))
      return
    }
    // Bulk replaces the entire list, so no day can collide with a kept row.
    if (formFor !== BULK_EDIT) {
      const otherDays = new Set<string>()
      rows.forEach((r, idx) => {
        if (formFor !== null && idx === formFor) return
        otherDays.add(normalizeDay(r.day))
      })
      if (selectedDays.some((d) => otherDays.has(normalizeDay(d)))) {
        setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DUPLICATE_ERROR'}))
        return
      }
    }
    const entries = selectedDays.map((day) => ({day, startTime, endTime, lectureDuration}))
    let next: TimingRow[]
    if (formFor === BULK_EDIT) {
      // Keep weekday order for the rebuilt list.
      next = WEEKDAYS.filter((d) => selectedDays.includes(d)).map((day) => ({
        day,
        startTime,
        endTime,
        lectureDuration,
      }))
    } else if (formFor !== null && formFor >= 0) {
      next = [...rows]
      next.splice(formFor, 1, ...entries)
    } else {
      next = [...rows, ...entries]
    }
    setRows(next)
    setFormError(null)
    setFormFor(null)
    // QA 18 Oct: Save saves (not just the draft), then the sheet shows the updated table.
    if (ONBOARDING_QA_OCT18) onCommit?.(next)
  }

  /** QA 18 Oct Cancel: drop the form's changes and go back to the unchanged table. */
  const closeForm = () => {
    setFormFor(null)
    setFormError(null)
  }

  /** QA: why Save is off for the lecture duration (it was disabled with no word): none, or too long. */
  const durationError = !ONBOARDING_QA_OCT18
    ? null
    : (form.lectureDuration ?? 0) < 1
      ? intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION_REQUIRED'},
          {max: MAX_LECTURE_MINUTES}
        )
      : (form.lectureDuration ?? 0) > MAX_LECTURE_MINUTES
        ? intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION_MAX'},
            {max: MAX_LECTURE_MINUTES}
          )
        : // Longer than the day's hours: said as it is typed, not only once Save is pressed.
          form.startTime &&
            form.endTime &&
            timeToMinutes(form.endTime) - timeToMinutes(form.startTime) >=
              0 &&
            timeToMinutes(form.endTime) - timeToMinutes(form.startTime) <
              (form.lectureDuration ?? 0)
          ? intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION_OVER_WINDOW'},
              {
                minutes: form.lectureDuration ?? 0,
                start: formatTime24to12(form.startTime),
                end: formatTime24to12(form.endTime),
              }
            )
          : null
  const formValid =
    form.selectedDays.length > 0 &&
    Boolean(form.startTime) &&
    Boolean(form.endTime) &&
    (form.lectureDuration ?? 0) >= 1 &&
    (form.lectureDuration ?? 0) <= MAX_LECTURE_MINUTES &&
    !durationError
  const dirty = !sameJson(rows, initial)
  // Days whose break would be orphaned by this draft: a break lives inside the day's school
  // hours, so a day with no timings can keep none. Save drops them (see the timings case in
  // Step2SectionEditor); this tells the user before they commit.
  const droppedBreakDays = breakDays.filter((day) => !usedDays.has(day))
  // QA 18 Oct: the form replaces the table, so the table and its actions show only while no
  // form is open (legacy: the table stays and the form is appended below it).
  const showTable = !ONBOARDING_QA_OCT18 || formFor === null
  // QA 18 Oct: the form's Save saves right away, so the breaks it would orphan are named in the
  // form itself (only Edit all can drop a day: add/edit never remove one).
  const formDroppedBreakDays =
    formFor === BULK_EDIT
      ? breakDays.filter((day) => usedDays.has(day) && !form.selectedDays.includes(day))
      : []
  const formTitle = intl.formatMessage({
    id:
      formFor === BULK_EDIT
        ? 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'
        : // QA: one row's Edit is an edit, not "Set Working Days and Timings".
          ONBOARDING_QA_OCT18 && formFor !== null && formFor >= 0
          ? 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_MODAL_TITLE'
          : 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_TITLE',
  })
  /** Day names for the dropped-breaks warning, in list order. */
  const dayNames = (days: string[]) =>
    days.map((day) => intl.formatMessage({id: `DAY.${day}` as any})).join(', ')

  return (
    <div ref={rootRef} {...(ONBOARDING_QA_OCT18 ? fixedFooterRootProps : editorRootProps)}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_QUESTION'})}
      </p>

      {rows.length === 0 && formFor === null && (
        <p style={helperStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EMPTY'})}
        </p>
      )}

      {showTable && rows.length > 0 && (
        // Header and rows share one grid template (.step2-edit-sheet-list) so the
        // column headings stay over their data at every width.
        <div
          className='step2-edit-sheet-list step2-edit-sheet-list--timings'
          style={{border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden'}}
        >
          <div className='step2-edit-sheet-list-head' style={{background: t.panelBg}}>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DAY'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_INSTITUTE'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ACTIONS'})}
            </div>
          </div>
          {rows.map((r, idx) => (
            <div
              key={`${r.day}-${idx}`}
              className='step2-edit-sheet-list-row'
              style={{borderTop: `1px solid ${t.border}`}}
            >
              <div>
                <span style={dayBadgeStyle(t)}>
                  {intl.formatMessage({id: `DAY.${normalizeDay(r.day)}` as any})}
                </span>
              </div>
              <div style={{color: t.text, fontSize: '0.9rem', fontWeight: 600, minWidth: 0}}>
                {formatTime24to12(r.startTime)}
                <span style={{color: t.muted, fontWeight: 400}}> — </span>
                {formatTime24to12(r.endTime)}
              </div>
              <div>
                <span style={pillStyle(t)}>
                  {r.lectureDuration ?? 0}{' '}
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MINS'})}
                </span>
              </div>
              <div className='d-flex gap-1'>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-edit'
                  aria-label={
                    ONBOARDING_QA_A11Y
                      ? intl.formatMessage(
                          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_DAY_ARIA'},
                          {day: intl.formatMessage({id: `DAY.${normalizeDay(r.day)}` as any})}
                        )
                      : 'Edit'
                  }
                  onClick={() => openEdit(idx)}
                >
                  <Pencil size={15} />
                </button>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-delete'
                  aria-label={
                    ONBOARDING_QA_A11Y
                      ? intl.formatMessage(
                          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DELETE_DAY_ARIA'},
                          {day: intl.formatMessage({id: `DAY.${normalizeDay(r.day)}` as any})}
                        )
                      : 'Delete'
                  }
                  // A day with breaks always asks: its breaks go too, and the delete saves.
                  onClick={() =>
                    skipDeleteConfirm && !deleteBreakWarning(idx)
                      ? deleteRow(idx)
                      : setConfirmDeleteIdx(idx)
                  }
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formFor === null && (
        <div className='d-flex gap-2 flex-wrap'>
          {usedDays.size < WEEKDAYS.length && (
            <button
              type='button'
              className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
              onClick={openAdd}
            >
              <Plus size={15} />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ADD_BTN'})}
            </button>
          )}
          {rows.length > 1 && (
            <button
              type='button'
              className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
              onClick={openBulkEdit}
            >
              <Pencil size={15} />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'})}
            </button>
          )}
        </div>
      )}

      {formFor !== null && (
        <div
          className='d-flex flex-column gap-3'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 14,
            padding: '1.1rem 1.2rem',
            background: t.panelBg,
          }}
        >
          <div className='d-flex align-items-center gap-2'>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'rgba(5,110,233,0.1)',
                color: t.isDark ? '#6ea8f7' : t.accent,
                flexShrink: 0,
              }}
            >
              <Clock size={15} />
            </span>
            <span
              ref={headingRef}
              // Focus target only (programmatic, on open), so no focus ring on the title.
              tabIndex={ONBOARDING_QA_OCT18 ? -1 : undefined}
              style={{
                color: t.text,
                fontWeight: 700,
                fontSize: '0.95rem',
                ...(ONBOARDING_QA_OCT18 ? {outline: 'none'} : {}),
              }}
            >
              {formTitle}
            </span>
          </div>
          {formFor === BULK_EDIT && (
            <p style={helperStyle(t)}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BULK_OVERWRITE_WARNING',
              })}
            </p>
          )}
          {ONBOARDING_QA_OCT18 && formFor === BULK_EDIT && rows.length > 0 && (
            // Edit all names what it overwrites: every day in the list with its current hours.
            <div>
              <span style={labelStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BULK_AFFECTED_LABEL'})}
              </span>
              <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>
                {rows.map((r, idx) => (
                  <li key={`${r.day}-${idx}`} className='d-flex align-items-center gap-2 flex-wrap'>
                    <span style={dayBadgeStyle(t)}>
                      {intl.formatMessage({id: `DAY.${normalizeDay(r.day)}` as any})}
                    </span>
                    <span style={{color: t.text, fontSize: '0.85rem', fontWeight: 600}}>
                      {formatTime24to12(r.startTime)}
                      <span style={{color: t.muted, fontWeight: 400}}> — </span>
                      {formatTime24to12(r.endTime)}
                    </span>
                    <span style={pillStyle(t)}>
                      {r.lectureDuration ?? 0}{' '}
                      {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MINS'})}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <label id={daysLabelId} style={labelStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_LABEL'})}
            </label>
            <p style={{...helperStyle(t), marginBottom: '0.6rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_HELPER'})}
            </p>
            <div
              className='d-flex gap-2 flex-wrap'
              role={ONBOARDING_QA_A11Y ? 'group' : undefined}
              aria-labelledby={ONBOARDING_QA_A11Y ? daysLabelId : undefined}
            >
              {WEEKDAYS.map((d, i) => (
                <DayChip
                  key={d}
                  label={DAY_LETTERS[i]}
                  name={intl.formatMessage({id: `DAY.${normalizeDay(d)}` as any})}
                  selected={form.selectedDays.includes(d)}
                  disabled={isDayDisabled(d)}
                  title={
                    isDayDisabled(d) && formFor === -1
                      ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DUPLICATE_ERROR'})
                      : undefined
                  }
                  // Add: the day already has a row; Edit all: only listed days; Edit: locked.
                  disabledReason={
                    formFor === -1
                      ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DUPLICATE_ERROR'})
                      : formFor === BULK_EDIT
                        ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.DAY_CHIP_NOT_LISTED'})
                        : intl.formatMessage(
                            {id: 'ONBOARDING_WIZARD.STEP2.DAY_CHIP_LOCKED'},
                            {
                              day: intl.formatMessage({
                                id: `DAY.${normalizeDay(rows[formFor ?? 0]?.day)}` as any,
                              }),
                            }
                          )
                  }
                  onToggle={() => {
                    setFormError(null)
                    setForm((s) => ({
                      ...s,
                      selectedDays: s.selectedDays.includes(d)
                        ? s.selectedDays.filter((x) => x !== d)
                        : [...s.selectedDays, d],
                    }))
                  }}
                  tokens={t}
                />
              ))}
            </div>
            {form.selectedDays.length === 0 && (
              <p
                className='mt-2'
                role={ONBOARDING_QA_A11Y ? 'alert' : undefined}
                style={errorStyle(t)}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_NO_DAY_ERROR'})}
              </p>
            )}
          </div>
          {/* Start/End share two equal grid columns (see .step2-edit-sheet-time-row).
              WizardTimePicker replaces the native time input: its OS-drawn picker
              (a full-width wheel sheet on iOS) could not be sized to the phone. */}
          <div className='step2-edit-sheet-time-row'>
            <div className='step2-edit-sheet-time-cell'>
              <label style={labelStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
              </label>
              <WizardTimePicker
                className='step2-edit-sheet-time-input'
                format='24h'
                icon={false}
                value={form.startTime}
                onChange={(v) => {
                  setForm((s) => ({...s, startTime: v}))
                  setFormError(null)
                }}
                style={inputStyle(t)}
                ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
              />
            </div>
            <div className='step2-edit-sheet-time-cell'>
              <label style={labelStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
              </label>
              <WizardTimePicker
                className='step2-edit-sheet-time-input'
                format='24h'
                icon={false}
                value={form.endTime}
                onChange={(v) => {
                  setForm((s) => ({...s, endTime: v}))
                  setFormError(null)
                }}
                style={inputStyle(t)}
                ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
              />
            </div>
            <div className='step2-edit-sheet-time-extra'>
              <label htmlFor={durationInputId} style={labelStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION'})}
              </label>
              {ONBOARDING_QA_OCT18 && (
                // QA: what the duration is, and its limits, before anything goes wrong.
                <p
                  id={`${durationInputId}-help`}
                  style={{...helperStyle(t), marginBottom: '0.4rem'}}
                >
                  {intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION_HELP'},
                    {max: MAX_LECTURE_MINUTES}
                  )}
                </p>
              )}
              <div className='d-flex align-items-center gap-2'>
                <input
                  id={durationInputId}
                  type='number'
                  min={1}
                  max={MAX_LECTURE_MINUTES}
                  value={form.lectureDuration || ''}
                  aria-invalid={durationError ? true : undefined}
                  aria-describedby={
                    ONBOARDING_QA_OCT18
                      ? `${durationInputId}-help${durationError ? ` ${durationInputId}-error` : ''}`
                      : undefined
                  }
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    setForm((s) => ({
                      ...s,
                      // QA: kept as typed (500 stays 500 and is said to be too long) instead of
                      // quietly becoming 240.
                      lectureDuration: isNaN(v)
                        ? 0
                        : ONBOARDING_QA_OCT18
                          ? Math.max(0, v)
                          : Math.max(0, Math.min(MAX_LECTURE_MINUTES, v)),
                    }))
                    setFormError(null)
                  }}
                  style={{...inputStyle(t), width: 80}}
                />
                <span style={{color: t.muted, fontSize: '0.85rem'}}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MINS'})}
                </span>
              </div>
              {ONBOARDING_QA_A11Y ? (
                // Always mounted, so the error is announced as it appears and changes while the
                // number is typed (polite: it does not cut the typing echo off).
                <div aria-live='polite'>
                  {durationError && (
                    <p
                      id={`${durationInputId}-error`}
                      className='mt-2 mb-0'
                      style={errorStyle(t)}
                    >
                      {durationError}
                    </p>
                  )}
                </div>
              ) : (
                durationError && (
                  <p id={`${durationInputId}-error`} className='mt-2 mb-0' style={errorStyle(t)}>
                    {durationError}
                  </p>
                )
              )}
            </div>
          </div>
          {ONBOARDING_QA_OCT18 && formDroppedBreakDays.length > 0 && (
            <p role='alert' style={errorStyle(t)}>
              {intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_REMOVE_DROPS_BREAKS'},
                {count: formDroppedBreakDays.length, days: dayNames(formDroppedBreakDays)}
              )}
            </p>
          )}
          {formError &&
            (ONBOARDING_QA_OCT18 || ONBOARDING_QA_A11Y ? (
              // Announced: Save kept the form open because of this.
              <p role='alert' style={errorStyle(t)}>
                {formError}
              </p>
            ) : (
              <p style={errorStyle(t)}>{formError}</p>
            ))}
          {/* QA 18 Oct: the sheet footer carries the form's Save/Cancel (one pair, not two). */}
          {!ONBOARDING_QA_OCT18 && (
            <div className='d-flex justify-content-end gap-2'>
              <button
                type='button'
                className='step2-edit-sheet-cancel step2-edit-sheet-btn-sm'
                onClick={() => {
                  setFormFor(null)
                  setFormError(null)
                }}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_CANCEL'})}
              </button>
              <button
                type='button'
                className='step2-edit-sheet-save step2-edit-sheet-btn-sm'
                onClick={applyForm}
                disabled={!formValid}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_SAVE'})}
              </button>
            </div>
          )}
        </div>
      )}

      {showTable && rows.length > 0 && (
        <p style={helperStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_APPLIED_INFO'})}
        </p>
      )}

      {showTable && droppedBreakDays.length > 0 && (
        <p role='alert' style={errorStyle(t)}>
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_REMOVE_DROPS_BREAKS'},
            {
              count: droppedBreakDays.length,
              days: droppedBreakDays
                .map((day) => intl.formatMessage({id: `DAY.${day}` as any}))
                .join(', '),
            }
          )}
        </p>
      )}

      <ConfirmDeleteDialog
        open={confirmDeleteIdx !== null}
        warning={deleteBreakWarning(confirmDeleteIdx)}
        onCancel={() => setConfirmDeleteIdx(null)}
        onConfirm={(dontShowAgain) => {
          const idx = confirmDeleteIdx
          setConfirmDeleteIdx(null)
          if (dontShowAgain) {
            writeSkipDeleteConfirm('timings')
            setSkipDeleteConfirm(true)
          }
          if (idx === null) return
          deleteRow(idx)
        }}
        tokens={t}
        intl={intl}
      />

      {!ONBOARDING_QA_OCT18 ? (
        <SheetFooter
          onCancel={onCancel}
          onSave={() => rows.length > 0 && onSave(rows)}
          saveDisabled={!dirty || rows.length === 0}
          isSubmitting={isSubmitting}
          tokens={t}
          intl={intl}
          // While the inline form is open its own Save is the action; two Save
          // buttons stacked on screen read as a mistake.
          hideSave={formFor !== null}
        />
      ) : formFor !== null ? (
        // QA 18 Oct: while the form is open the one footer is the form's Save/Cancel.
        <SheetFooter
          fixed
          ariaLabel={formTitle}
          onCancel={closeForm}
          onSave={applyForm}
          saveDisabled={!formValid}
          isSubmitting={isSubmitting}
          tokens={t}
          intl={intl}
        />
      ) : (
        <SheetFooter
          fixed
          onCancel={onCancel}
          onSave={() => rows.length > 0 && onSave(rows)}
          saveDisabled={!dirty || rows.length === 0}
          isSubmitting={isSubmitting}
          tokens={t}
          intl={intl}
        />
      )}
    </div>
  )
}

/* -------------------------------- Breaks ------------------------------------ */

const BreaksEditor: FC<
  EditorCommon & {
    initial: BreakRow[]
    timings: TimingRow[]
    onSave: (breaks: BreakRow[]) => void
    /** QA 18 Oct: save the list without closing the sheet (the form's Save). */
    onCommit?: (breaks: BreakRow[]) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, timings, onSave, onCommit}) => {
  const [rows, setRows] = useState<BreakRow[]>(initial)
  // null = form closed; '' = adding; BULK_EDIT = editing every day's breaks at
  // once; '<DAY>' = editing that day's breaks
  const BULK_EDIT = '*'
  const [formFor, setFormFor] = useState<string | null>(initial.length === 0 ? '' : null)
  const {rootRef, headingRef} = useTableFormSwap(formFor)
  // Day awaiting delete confirmation, or null.
  const [confirmDeleteDay, setConfirmDeleteDay] = useState<string | null>(null)
  // "Do not show again" ticked earlier (persisted): delete days without asking.
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(() => readSkipDeleteConfirm('breaks'))

  const deleteDay = (day: string) => {
    const next = rows.filter((b) => normalizeDay(b.day) !== day)
    setRows(next)
    if (formFor === day) setFormFor(null)
    // QA: a delete saves at once, as the form's Save does (it used to wait for the footer's Save
    // and was lost when the sheet closed). No breaks at all is a valid answer.
    if (ONBOARDING_QA_OCT18) onCommit?.(next)
  }

  /** Days that can hold a break come pre-selected in the add form: every day
   *  with school timings, minus days that already have a break in the draft. */
  const defaultBreakDays = (currentRows: BreakRow[]) => {
    const withTimings = new Set(timings.map((x) => normalizeDay(x.day)))
    const withBreaks = new Set(currentRows.map((b) => normalizeDay(b.day)))
    return WEEKDAYS.filter((d) => withTimings.has(d) && !withBreaks.has(d))
  }

  // One slot per break; edit mode loads every break of the day so a day with
  // two breaks can have both changed (it used to open only the first).
  const [form, setForm] = useState<{selectedDays: string[]; slots: BreakSlot[]}>(() => {
    const prefill = getMidpointBreak(timings)
    return {
      selectedDays: defaultBreakDays(initial),
      slots: [{startTime: prefill.startTime, endTime: prefill.endTime}],
    }
  })
  const [formError, setFormError] = useState<string | null>(null)
  const daysLabelId = useId()

  const updateSlot =(idx: number, patch: Partial<BreakSlot>) => {
    setForm((s) => ({...s, slots: s.slots.map((x, i) => (i === idx ? {...x, ...patch} : x))}))
    setFormError(null)
  }
  /** New slot starts where the last one ends and keeps its length. */
  const addSlot = () => {
    setForm((s) => {
      const last = s.slots[s.slots.length - 1]
      if (!last?.startTime || !last?.endTime) {
        return {...s, slots: [...s.slots, {startTime: '', endTime: ''}]}
      }
      const length = Math.max(timeToMinutes(last.endTime) - timeToMinutes(last.startTime), 15)
      const start = timeToMinutes(last.endTime)
      const end = Math.min(start + length, 23 * 60 + 59)
      return {
        ...s,
        slots: [...s.slots, {startTime: minutesToTime(start), endTime: minutesToTime(end)}],
      }
    })
    setFormError(null)
  }
  const removeSlot = (idx: number) => {
    setForm((s) => ({...s, slots: s.slots.filter((_, i) => i !== idx)}))
    setFormError(null)
  }

  const timingDays = useMemo(() => new Set(timings.map((x) => normalizeDay(x.day))), [timings])

  // Weekday order, breaks within a day by start time — so editing Tuesday does
  // not drop it to the bottom of the list (the edited day's rows are re-added).
  const grouped = useMemo(() => {
    const byDay: Record<string, BreakRow[]> = {}
    sortBreaks(rows).forEach((b) => {
      const day = normalizeDay(b.day) || 'MONDAY'
      if (!byDay[day]) byDay[day] = []
      byDay[day].push(b)
    })
    return Object.entries(byDay)
  }, [rows])

  const openAdd = () => {
    const prefill = getMidpointBreak(timings)
    setForm({
      selectedDays: defaultBreakDays(rows),
      slots: [{startTime: prefill.startTime, endTime: prefill.endTime}],
    })
    setFormError(null)
    setFormFor('')
  }

  const openEdit = (day: string) => {
    const dayBreaks = rows
      .filter((b) => normalizeDay(b.day) === day)
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
    if (dayBreaks.length === 0) return
    setForm({
      selectedDays: [day],
      slots: dayBreaks.map((b) => ({startTime: b.startTime, endTime: b.endTime})),
    })
    setFormError(null)
    setFormFor(day)
  }

  /**
   * Bulk edit: one form pre-selecting every day that has a break, whose Save
   * REPLACES the whole list — the same break slot(s) applied to all selected
   * days (chips stay toggleable, so days can be added or dropped in the same
   * pass). Seeded from the first day's breaks.
   */
  const openBulkEdit = () => {
    const days = WEEKDAYS.filter((d) => rows.some((b) => normalizeDay(b.day) === d))
    const firstDay = days[0]
    const seed = rows
      .filter((b) => normalizeDay(b.day) === firstDay)
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
    const prefill = getMidpointBreak(timings)
    setForm({
      selectedDays: days,
      slots:
        seed.length > 0
          ? seed.map((b) => ({startTime: b.startTime, endTime: b.endTime}))
          : [{startTime: prefill.startTime, endTime: prefill.endTime}],
    })
    setFormError(null)
    setFormFor(BULK_EDIT)
  }

  const applyForm = () => {
    const {selectedDays, slots} = form
    if (selectedDays.length === 0 || slots.some((s) => !s.startTime || !s.endTime)) return
    // Off-days cannot have breaks.
    if (selectedDays.some((d) => !timingDays.has(normalizeDay(d)))) {
      setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DAY_DISABLED'}))
      return
    }
    const ranges = slots
      .map((s) => ({start: timeToMinutes(s.startTime), end: timeToMinutes(s.endTime)}))
      .sort((a, b) => a.start - b.start)
    if (ranges.some((r) => r.end <= r.start)) {
      setFormError(
        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_END_AFTER_START'})
      )
      return
    }
    // Every break must fall completely inside that day's institute timings.
    const outsideDay = selectedDays.find((d) => {
      const timing = timings.find((x) => normalizeDay(x.day) === normalizeDay(d))
      if (!timing?.startTime || !timing?.endTime) return false
      const dayStart = timeToMinutes(timing.startTime)
      const dayEnd = timeToMinutes(timing.endTime)
      return ranges.some((r) => r.start < dayStart || r.end > dayEnd)
    })
    if (outsideDay) {
      const timing = timings.find((x) => normalizeDay(x.day) === normalizeDay(outsideDay))
      setFormError(
        intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_OUTSIDE_HOURS'},
          {
            day: intl.formatMessage({id: `DAY.${normalizeDay(outsideDay)}` as any}),
            start: formatTime24to12(timing?.startTime ?? ''),
            end: formatTime24to12(timing?.endTime ?? ''),
          }
        )
      )
      return
    }
    // Reject overlaps: between the form's own breaks, and with breaks that stay
    // (the edited day's breaks are replaced wholesale; bulk replaces every day).
    const selfClash = ranges.some((r, i) => i > 0 && r.start < ranges[i - 1].end)
    const kept =
      formFor === BULK_EDIT
        ? []
        : rows.filter((b) => (formFor ? normalizeDay(b.day) !== formFor : true))
    const clash =
      selfClash ||
      selectedDays.some((d) =>
        kept.some(
          (b) =>
            normalizeDay(b.day) === normalizeDay(d) &&
            ranges.some(
              (r) => r.start < timeToMinutes(b.endTime) && r.end > timeToMinutes(b.startTime)
            )
        )
      )
    if (clash) {
      setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_OVERLAP_ERROR'}))
      return
    }
    const entries = selectedDays.flatMap((day) =>
      slots.map((s) => ({day, startTime: s.startTime, endTime: s.endTime}))
    )
    const next = sortBreaks(formFor ? [...kept, ...entries] : [...rows, ...entries])
    setRows(next)
    setFormError(null)
    setFormFor(null)
    // QA 18 Oct: Save saves (not just the draft), then the sheet shows the updated table.
    if (ONBOARDING_QA_OCT18) onCommit?.(next)
  }

  /** QA 18 Oct Cancel: drop the form's changes and go back to the unchanged table. */
  const closeForm = () => {
    setFormFor(null)
    setFormError(null)
  }

  const formValid =
    form.selectedDays.length > 0 && form.slots.every((s) => Boolean(s.startTime && s.endTime))
  // Order-insensitive: the list is re-sorted after every inline save.
  const dirty = !sameJson(sortBreaks(rows), sortBreaks(initial))
  // QA 18 Oct: the form replaces the table, so the table and its actions show only while no
  // form is open (legacy: the table stays and the form is appended below it).
  const showTable = !ONBOARDING_QA_OCT18 || formFor === null
  const formTitle = intl.formatMessage({
    id:
      formFor === BULK_EDIT
        ? 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'
        : // QA: one day's Edit is an edit, not "Add Break Timing".
          ONBOARDING_QA_OCT18 && formFor
          ? 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_EDIT_MODAL_TITLE'
          : 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_MODAL_TITLE',
  })

  return (
    <div ref={rootRef} {...(ONBOARDING_QA_OCT18 ? fixedFooterRootProps : editorRootProps)}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_SECTION_TITLE'})}
      </p>

      {rows.length === 0 && formFor === null && (
        <p style={helperStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_EMPTY'})}
        </p>
      )}

      {showTable && grouped.length > 0 && (
        // Same shared grid as the timings list (three columns here).
        <div
          className='step2-edit-sheet-list step2-edit-sheet-list--breaks'
          style={{border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden'}}
        >
          <div className='step2-edit-sheet-list-head' style={{background: t.panelBg}}>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DAY'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_COLUMN'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ACTIONS'})}
            </div>
          </div>
          {grouped.map(([day, breaks]) => (
            <div
              key={day}
              className='step2-edit-sheet-list-row'
              style={{borderTop: `1px solid ${t.border}`}}
            >
              <div>
                <span style={dayBadgeStyle(t)}>
                  {intl.formatMessage({id: `DAY.${day}` as any})}
                </span>
              </div>
              <div style={{color: t.text, fontSize: '0.9rem', fontWeight: 600, minWidth: 0}}>
                {breaks
                  .map((b) => `${formatTime24to12(b.startTime)} — ${formatTime24to12(b.endTime)}`)
                  .join(', ')}
              </div>
              <div className='d-flex gap-1'>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-edit'
                  aria-label={
                    ONBOARDING_QA_A11Y
                      ? intl.formatMessage(
                          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_EDIT_DAY_ARIA'},
                          {day: intl.formatMessage({id: `DAY.${day}` as any})}
                        )
                      : 'Edit'
                  }
                  onClick={() => openEdit(day)}
                >
                  <Pencil size={15} />
                </button>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-delete'
                  aria-label={
                    ONBOARDING_QA_A11Y
                      ? intl.formatMessage(
                          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DELETE_DAY_ARIA'},
                          {day: intl.formatMessage({id: `DAY.${day}` as any})}
                        )
                      : 'Delete'
                  }
                  onClick={() => (skipDeleteConfirm ? deleteDay(day) : setConfirmDeleteDay(day))}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formFor === null && (
        <div className='d-flex gap-2 flex-wrap'>
          <button
            type='button'
            className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
            onClick={openAdd}
          >
            <Plus size={15} />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_ADD_BTN'})}
          </button>
          {grouped.length > 1 && (
            <button
              type='button'
              className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
              onClick={openBulkEdit}
            >
              <Pencil size={15} />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'})}
            </button>
          )}
        </div>
      )}

      {formFor !== null && (
        <div
          className='d-flex flex-column gap-3'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 14,
            padding: '1.1rem 1.2rem',
            background: t.panelBg,
          }}
        >
          <div className='d-flex align-items-center gap-2'>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'rgba(5,110,233,0.1)',
                color: t.isDark ? '#6ea8f7' : t.accent,
                flexShrink: 0,
              }}
            >
              <Coffee size={15} />
            </span>
            <span
              ref={headingRef}
              // Focus target only (programmatic, on open), so no focus ring on the title.
              tabIndex={ONBOARDING_QA_OCT18 ? -1 : undefined}
              style={{
                color: t.text,
                fontWeight: 700,
                fontSize: '0.95rem',
                ...(ONBOARDING_QA_OCT18 ? {outline: 'none'} : {}),
              }}
            >
              {formTitle}
            </span>
          </div>
          {formFor === BULK_EDIT && (
            <p style={helperStyle(t)}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_BULK_OVERWRITE_WARNING',
              })}
            </p>
          )}
          {ONBOARDING_QA_OCT18 && formFor === BULK_EDIT && grouped.length > 0 && (
            // Edit all names what it overwrites: every day's current breaks.
            <div>
              <span style={labelStyle(t)}>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_BULK_AFFECTED_LABEL',
                })}
              </span>
              <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>
                {grouped.map(([day, breaks]) => (
                  <li key={day} className='d-flex align-items-center gap-2 flex-wrap'>
                    <span style={dayBadgeStyle(t)}>
                      {intl.formatMessage({id: `DAY.${day}` as any})}
                    </span>
                    <span style={{color: t.text, fontSize: '0.85rem', fontWeight: 600}}>
                      {breaks
                        .map(
                          (b) => `${formatTime24to12(b.startTime)} — ${formatTime24to12(b.endTime)}`
                        )
                        .join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <label id={daysLabelId} style={labelStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_LABEL'})}
            </label>
            <div
              className='d-flex gap-2 flex-wrap'
              role={ONBOARDING_QA_A11Y ? 'group' : undefined}
              aria-labelledby={ONBOARDING_QA_A11Y ? daysLabelId : undefined}
            >
              {WEEKDAYS.map((d, i) => {
                // Only days that have institute timings can hold a break; edit
                // mode locks the chips to the day being edited; bulk mode frees
                // every day that has timings.
                const disabled =
                  formFor && formFor !== BULK_EDIT
                    ? normalizeDay(d) !== formFor
                    : !timingDays.has(normalizeDay(d))
                return (
                  <DayChip
                    key={d}
                    label={DAY_LETTERS[i]}
                    name={intl.formatMessage({id: `DAY.${normalizeDay(d)}` as any})}
                    selected={form.selectedDays.includes(d)}
                    disabled={disabled}
                    title={
                      disabled && (!formFor || formFor === BULK_EDIT)
                        ? intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DAY_DISABLED',
                          })
                        : undefined
                    }
                    // Add / Edit all: no school timings that day; Edit: locked to the edited day.
                    disabledReason={
                      !formFor || formFor === BULK_EDIT
                        ? intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DAY_DISABLED',
                          })
                        : intl.formatMessage(
                            {id: 'ONBOARDING_WIZARD.STEP2.DAY_CHIP_LOCKED'},
                            {day: intl.formatMessage({id: `DAY.${formFor}` as any})}
                          )
                    }
                    onToggle={() => {
                      setFormError(null)
                      setForm((s) => ({
                        ...s,
                        selectedDays: s.selectedDays.includes(d)
                          ? s.selectedDays.filter((x) => x !== d)
                          : [...s.selectedDays, d],
                      }))
                    }}
                    tokens={t}
                  />
                )
              })}
            </div>
          </div>
          {form.slots.map((slot, idx) => (
            // Same equal-column grid as the timings form; the remove button takes a
            // fixed third column so it never steals width from either time input.
            <div key={idx} className='step2-edit-sheet-time-row'>
              <div className='step2-edit-sheet-time-cell'>
                <label style={labelStyle(t)}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
                </label>
                <WizardTimePicker
                  className='step2-edit-sheet-time-input'
                  format='24h'
                  icon={false}
                  value={slot.startTime}
                  onChange={(v) => updateSlot(idx, {startTime: v})}
                  style={inputStyle(t)}
                  ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
                />
              </div>
              <div className='step2-edit-sheet-time-cell'>
                <label style={labelStyle(t)}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
                </label>
                <WizardTimePicker
                  className='step2-edit-sheet-time-input'
                  format='24h'
                  icon={false}
                  value={slot.endTime}
                  onChange={(v) => updateSlot(idx, {endTime: v})}
                  style={inputStyle(t)}
                  ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
                />
              </div>
              {form.slots.length > 1 && (
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-delete step2-edit-sheet-time-remove'
                  aria-label={
                    ONBOARDING_QA_A11Y
                      ? intl.formatMessage(
                          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_REMOVE_SLOT_ARIA'},
                          {number: idx + 1}
                        )
                      : 'Remove'
                  }
                  onClick={() => removeSlot(idx)}
                >
                  <X size={15} />
                </button>
              )}
            </div>
          ))}
          <button
            type='button'
            className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2 align-self-start'
            onClick={addSlot}
          >
            <Plus size={14} />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_ADD_ANOTHER_SLOT'})}
          </button>
          {formError &&
            (ONBOARDING_QA_OCT18 || ONBOARDING_QA_A11Y ? (
              // Announced: Save kept the form open because of this.
              <p role='alert' style={errorStyle(t)}>
                {formError}
              </p>
            ) : (
              <p style={errorStyle(t)}>{formError}</p>
            ))}
          {/* QA 18 Oct: the sheet footer carries the form's Save/Cancel (one pair, not two). */}
          {!ONBOARDING_QA_OCT18 && (
            <div className='d-flex justify-content-end gap-2'>
              <button
                type='button'
                className='step2-edit-sheet-cancel step2-edit-sheet-btn-sm'
                onClick={() => {
                  setFormFor(null)
                  setFormError(null)
                }}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_CANCEL'})}
              </button>
              <button
                type='button'
                className='step2-edit-sheet-save step2-edit-sheet-btn-sm'
                onClick={applyForm}
                disabled={!formValid}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_SAVE'})}
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        open={confirmDeleteDay !== null}
        onCancel={() => setConfirmDeleteDay(null)}
        onConfirm={(dontShowAgain) => {
          const day = confirmDeleteDay
          setConfirmDeleteDay(null)
          if (dontShowAgain) {
            writeSkipDeleteConfirm('breaks')
            setSkipDeleteConfirm(true)
          }
          if (day === null) return
          deleteDay(day)
        }}
        tokens={t}
        intl={intl}
      />

      {!ONBOARDING_QA_OCT18 ? (
        <SheetFooter
          onCancel={onCancel}
          onSave={() => onSave(rows)}
          saveDisabled={!dirty}
          isSubmitting={isSubmitting}
          tokens={t}
          intl={intl}
          // While the inline form is open its own Save is the action, and with
          // nothing listed (and nothing to clear) there is nothing to commit —
          // either way the footer keeps just Cancel.
          hideSave={formFor !== null || (rows.length === 0 && initial.length === 0)}
        />
      ) : formFor !== null ? (
        // QA 18 Oct: while the form is open the one footer is the form's Save/Cancel.
        <SheetFooter
          fixed
          ariaLabel={formTitle}
          onCancel={closeForm}
          onSave={applyForm}
          saveDisabled={!formValid}
          isSubmitting={isSubmitting}
          tokens={t}
          intl={intl}
        />
      ) : (
        <SheetFooter
          fixed
          onCancel={onCancel}
          onSave={() => onSave(rows)}
          saveDisabled={!dirty}
          isSubmitting={isSubmitting}
          tokens={t}
          intl={intl}
          // With nothing listed (and nothing to clear) there is nothing to commit.
          hideSave={rows.length === 0 && initial.length === 0}
        />
      )}
    </div>
  )
}

export default Step2SectionEditor
